import { AfterViewInit, Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { CurrentUserService } from 'src/app/user/services';
import { Sequence } from '../models/sequence';
import { SequencesService } from '../services/sequences.service';
import { GridService } from 'src/app/shared/grid/grid.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoColumnFixingModule, DxoGroupPanelModule, DxiColumnModule, DxiValidationRuleModule, DxoFormItemModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { DxTemplateModule } from 'devextreme-angular/core';

@Component({
    selector: 'app-sequences',
    templateUrl: './sequences.component.html',
    styleUrls: ['./sequences.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoColumnFixingModule, DxoGroupPanelModule, DxiColumnModule, DxiValidationRuleModule, DxoFormItemModule, DxiButtonModule, DxTemplateModule]
})
export class SequencesComponent implements OnInit, AfterViewInit {
    allowAdding = false;
    allowShowLogHeader = false;
    genericModalService: any;
    sequences: Sequence[] = [];

    sequenceMaxvalue = 9007199254740991;
    sequenceMinvalue = -9007199254740991;
    defaulMinvalue = 0;

    constructor(
        private sequencesService: SequencesService,
        private loadingPanelService: LoadingPanelService,
        private toastService: ToastService,
        private currentUserService: CurrentUserService,
        private gridService: GridService) {
    }

    ngOnInit(): void {
        this.allowAdding = this.currentUserService.hasPermission('Sequences_Action_Create');
    }

    async ngAfterViewInit(): Promise<void> {
        await this.refresh();
    }

    async refresh(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');
            this.sequences = [];

            await Promise.all([
                this.sequencesService.getSequences().then(result => { this.sequences = result; })
            ]);

        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to load data...', ToastType.error);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    onInitNewRow(e: any): void {
        e.data = {
            Id: null,
            Name: '',
            IsSystemSequence: false,
            Increment: 1,
            NextValue: 1,
            MaxValue: this.sequenceMaxvalue,
            MinValue: this.defaulMinvalue
        } as Sequence;
        e.component.columnOption('Name', 'allowEditing', true);
    }

    onEditingStart(e: any): void {
        e.component.columnOption('Name', 'allowEditing', false);
    }

    allowUpdating = (e) => {
        const allowUpdating = this.currentUserService.hasPermission('Sequences_Action_Edit');
        const isSystem: boolean = e.row.data.IsSystemSequence;
        return (!isSystem && allowUpdating);
    };

    allowDeleting = (e) => {
        const allowDeleting = this.currentUserService.hasPermission('Sequences_Action_Delete');
        const isSystem: boolean = e.row.data.IsSystemSequence;
        return (!isSystem && allowDeleting);
    };

    allowShowLog = (e) => {
        const allowShowLog = this.currentUserService.hasPermission('Sequences_Action_ShowLog');
        const isSystem: boolean = e.row.data.IsSystemSequence;
        return (!isSystem && allowShowLog);
    };

    onRowInserting = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as Sequence, item => this.saveSequence(item));
    };

    onRowUpdating = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.key, ...e.newData } as Sequence, item => this.saveSequence(item), e.newData);
    };

    onRowRemoving = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as Sequence, item => this.deleteSequence(item));
    };

    async deleteSequence(seq: Sequence): Promise<Sequence> {
        await this.sequencesService.deleteSequence(seq.Id, _ => false);
        this.toastService.showToast('Sequence deleted...', ToastType.success);
        return seq;
    }

    async saveSequence(seq: Sequence): Promise<Sequence> {
        let result: Sequence = null;
        if(seq.Increment === 0){
            throw new Error('Increment value cannot be 0');
        }
        if (seq.NextValue > this.sequenceMaxvalue || seq.MaxValue > this.sequenceMaxvalue) {
            throw new Error(`Current Value or Max Value is bigger than the allowed max value of ${this.sequenceMaxvalue}`);
        }
        if (seq.NextValue < this.defaulMinvalue || seq.MinValue < this.defaulMinvalue) {
            throw new Error(`Current Value or Min Value is smaller than the allowed min value of ${this.defaulMinvalue}`);
        }
        if ((seq.NextValue - seq.Increment) < seq.MinValue) {
            throw new Error(`The Next Value: ${seq.NextValue} must be greater than the Minimum Value: ${seq.MinValue} plus the Increment value: ${seq.Increment}`);
        }
        result = await this.sequencesService.saveSequence(seq, false);
        this.toastService.showToast('Sequence saved...', ToastType.success);
        return result;
    }
}

